/**
 * Settings → Storage client API
 * (docs/plans/2026-08-05_SETTINGS_STORAGE_TAB.md §4.1).
 *
 * One read endpoint, membership-gated — works for orgs AND personal tenants
 * (the `/orgs/` prefix is a naming artifact, not a tenant-type restriction).
 */
import { request } from '@papan/api-client'

export interface StorageCategory {
  /** 'documents' | 'images' | 'video' | 'audio' | 'other' — fixed order. */
  key: string
  bytes: number
  files: number
}

export interface ReclaimableBucket {
  bytes: number
  items: number
}

/** #758: what the tenant has PROMISED its members. Admin-only, so `null`
 *  for everyone else — not 0, which would read as "nothing promised". */
export interface ChatStorageAllocated {
  promisedBytes: number
}

export interface StorageCompositionResponse {
  quotaBytes: number
  /** Live (non-folder) file bytes — always the exact SQL sum; equals the
   *  category-bucket total by construction. Headline = live + reclaimable. */
  live: { bytes: number }
  categories: StorageCategory[]
  /** NOT inside tree.bytes — render as "not counted above". The versions
   *  figure already excludes live files' current content (§2.8). */
  reclaimable: {
    bin: ReclaimableBucket
    versions: ReclaimableBucket
    quarantine: ReclaimableBucket
  }
  /** Exact-duplicate aggregate (followups §2.1) — set count + the bytes
   *  freed by deleting all-but-one copy per set. Aggregate only; names
   *  live behind the owner/admin duplicates endpoint. */
  duplicates: { sets: number; wastedBytes: number }
  /** Session-private attachments (0218, #639) — dedup-aware aggregate byte
   *  count only: no per-item, no per-session breakdown, no attribution, and
   *  deliberately NOT under `reclaimable` — there is no delete/reclaim
   *  action for this bucket from the storage screen (cleaned only via the
   *  session lifecycle). Still counts toward `usedTotal`/`nearFull` since
   *  it counts toward the enforced quota. Optional on the client type only
   *  (deploy-skew guard, #639 final review): the server always sends it —
   *  this only hardens the SPA-ahead-of-API window during a rolling
   *  deploy, where an old API instance's response omits the field. */
  sessionAttachments?: { bytes: number }
  /** What THIS caller may do with the reclaimable rows. Each mirrors its
   *  own endpoint's gate, so the page never offers a destructive control
   *  that can only 403. Absent from a pre-flag API → treated as allowed,
   *  which is the old behaviour (the server still refuses). */
  can?: {
    emptyBin: boolean
    clearVersions: boolean
    /** #1102 item 4: `manage_file_versions:read` — the old-versions review listing (absent from an older server). */
    reviewVersions?: boolean
    reviewDuplicates: boolean
    /**
     * D13: chat storage is a capability, and read ≠ write — a delegate may be
     *  allowed to SEE who is using what without being allowed to change
     *  anyone's limit.
     *
     * ⚠️ READ FAIL-CLOSED, unlike every other flag on this page.
     *
     * `emptyBin`, `clearVersions` and `reviewDuplicates` are all read
     * `?? true`: an older API that does not send them means "assume allowed",
     * and the worst case is a button that 403s. These two are read
     * `?? clearVersions ?? false` instead, and the difference is deliberate
     * (round 4, finding 7 asked for one or the other; this is the choice).
     *
     * The middle term handles the ordinary skew — an API with `can` but
     * without these two fields is an API whose gate is still the old
     * owner/admin role test, which is exactly what `clearVersions` carries.
     * The `false` tail only applies when there is no `can` object at all, and
     * there the trade is: an owner briefly loses the Chat storage org view
     * during a rolling restart, versus someone who should not have them being
     * offered controls that move other people's limits. Losing a view for a
     * minute is the cheaper mistake.
     */
    viewChatStorage?: boolean
    manageChatStorage?: boolean
  }
  /** #758. Absent on an older API instance (deploy skew), null for a
   *  non-admin — both mean "say nothing about what has been promised".
   *  (It fed an over-allocation mark on the storage bar until 07e5b964b
   *  took that out; now it is only the hero's "X promised to members"
   *  clause and the Adjust dialog's allocation line.) */
  chatStorageAllocated?: ChatStorageAllocated | null
}

export interface DuplicateSet {
  hash: string
  size: number
  copies: number
  wastedBytes: number
  files: CategoryFileRow[]
}

export interface DuplicatesResponse {
  /** Most-wasteful first, top 50. */
  sets: DuplicateSet[]
  totalSets: number
  totalWastedBytes: number
}

export interface LargestFolder {
  folderId: string
  /** Full VFS path from the drive root ('papan/Library/dev'). */
  path: string
  /** Subtree total — every file under it, at any depth. */
  bytes: number
  files: number
}

export interface VersionFileGroup {
  fileId: string
  name: string
  folderId: string | null
  folder: string | null
  totalBytes: number
  items: number
  /** Newest first. */
  versions: Array<{ id: string; size: number; createdAt: string; source: string | null; description: string | null }>
}

export interface VersionsResponse {
  /** Biggest per-file totals first, top 100 files. */
  files: VersionFileGroup[]
  /** Tenant-wide — matches the "Old versions" reclaimable line exactly. */
  totalItems: number
  totalBytes: number
  /** More files carry old versions than the payload lists (past the 100-file
   *  cap). The modal says so: the totals are tenant-wide and a selection
   *  cannot reach the unlisted files, though clear-all still does. */
  truncated: boolean
}

/**
 * What "Clear old versions" actually freed. `blobsRemoved` is the count of
 * content blobs whose LAST reference the purge removed — i.e. bytes that
 * genuinely left the disk, as opposed to `freedBytes`, which is the counted
 * quota the version rows were charging for. The two differ whenever content
 * is deduplicated: purging a version whose bytes another row still uses
 * frees quota without freeing disk. The field was missing from this type
 * while the route returned it, so the UI could not report the one number
 * the reclamation work exists to produce (review 2026-08-06).
 */
export interface PurgeVersionsResult {
  success: boolean
  deleted: number
  freedBytes: number
  blobsRemoved: number
}

export interface CategoryFileRow {
  id: string
  name: string
  bytes: number
  /** Parent folder — the modal's grouping key + label. `folder` is the full
   *  VFS path from the drive root ('papan/ttt'); null on a broken tree. */
  folderId: string | null
  folder: string | null
  /** ISO timestamp of the last content change — the disambiguator for
   *  same-named rows (conflict renames, zip-download names). */
  modifiedAt: string
  /** Whether THIS caller may delete the row (followups §3.3). A member can
   *  legitimately read a file they cannot write, and the delete endpoint
   *  enforces write regardless — so the modal disables the control rather
   *  than letting the submit fail. Absent from a pre-§3.3 API, where the
   *  listing was owner/admin-only and every row was deletable. */
  canDelete?: boolean
}

export interface CategoryFilesResponse {
  category: string
  /** Bucket-wide count/bytes — NOT capped by the 500-row payload limit. */
  total: number
  totalBytes: number
  truncated: boolean
  /** Rows were withheld from THIS caller by the permission filter — the
   *  modal distinguishes "empty bucket" from "nothing you can see". */
  filtered?: boolean
  /** Largest first. */
  files: CategoryFileRow[]
}

/**
 * The tenant's own version-retention CEILING (#452 Task A7/A8) plus the
 * hierarchical per-kind/per-extension overrides on top of it (#590).
 * `ceiling` is what THIS tenant stored (`null` = inherit), `platformDefault`
 * is the deployment-wide setting it would fall back to, and `resolved` is
 * what is actually in force at the CEILING layer —
 * `tenant ceiling → platform default → shipped default`, resolved
 * server-side by `resolveVersionRetention` so this client never has to
 * re-implement the chain.
 *
 * `kinds`/`exts` are the tenant's OWN raw overrides (never merged with the
 * platform's) — round-tripped on Save so the editor can tell "the tenant set
 * this" from "this is inherited". `platformKinds` is the platform's raw
 * per-kind defaults, so a row with no tenant override can still show what it
 * would resolve to without re-deriving the merged blob
 * (`resolveTenantRetentionConfig`'s `{ ...platformKinds, ...tenantKinds }`).
 */
export interface VersionRetentionResponse {
  ceiling: number | null
  platformDefault: number | null
  resolved: number
  kinds: Record<string, number>
  exts: Record<string, number>
  platformKinds: Record<string, number>
  /** The app-version ladder's tenant layer (mirrors `ceiling` above, but has
   *  no platform layer): `null` = inherit the shipped default (50).
   *  `appResolved` is what is actually in force at this layer —
   *  `appCeiling ?? 50` — resolved server-side so this client never has to
   *  re-implement the chain. */
  appCeiling: number | null
  appResolved: number
}

/** Staged `PUT` payload — always the WHOLE `kinds`/`exts` object, never a
 *  partial merge (mirrors the route's own contract). Omitting `kinds`/`exts`
 *  clears them, same as passing `{}`. Same for `appCeiling`: omitting it
 *  clears the tenant's app-version ceiling back to inherit. */
export interface VersionRetentionUpdate {
  ceiling: number | null
  kinds?: Record<string, number>
  exts?: Record<string, number>
  appCeiling?: number | null
}

export interface VersionRetentionOverrideFile {
  fileId: string
  name: string
  path: string
  maxVersions: number
}

export interface VersionRetentionOverridesResponse {
  files: VersionRetentionOverrideFile[]
}

export interface AppVersionRetentionOverride {
  pdappId: string
  name: string
  path: string
  maxVersions: number
}

export interface AppVersionRetentionOverridesResponse {
  apps: AppVersionRetentionOverride[]
}

/**
 * Distinct file types actually present on the tenant's live drive (#590
 * follow-up) — drives the "by file type" master-detail rail so it shows only
 * what the drive contains instead of the full curated-kind taxonomy. `kind`
 * is a `FileKind` string (never `'other'` — the route drops it, it has no
 * configurable extension set); `extsPresent` is that kind's present
 * extensions, sorted. `pdappPresent` is independent of `kinds` — `pdapp` has
 * no extension children of its own.
 */
export interface DriveFileTypes {
  kinds: { kind: string; extsPresent: string[] }[]
  pdappPresent: boolean
}

/**
 * Sync exclusions (#616) — which folders the AI sandbox declines to walk, so
 * a `node_modules` or a build directory is not hashed, versioned and written
 * back on every command.
 *
 * A SEPARATE endpoint from version retention, deliberately: that route
 * replaces its whole config object on every call, so folding folder rules in
 * as a fifth key would mean any client that saved retention without sending
 * them silently cleared them.
 */
/**
 * One folder rule: a pattern, and how many versions the files under it keep.
 *
 * `1..maxVersions`. There is no `0` and no "off": a file that exists is
 * version 1, and a folder rule caps HISTORY — it never stops a folder being
 * synced, listed or charged for. An earlier shape had a fourth state that did,
 * and it is gone; `FolderRetention` in `@papan/types` records why.
 */
export interface SyncExclusionRule {
  pattern: string
  versions: number
}

/** A rule an EARLIER rule already covers — it can never win, because the
 *  first match in list order decides. Surfaced, never silently dropped: from
 *  the user's side a dead rule and one that has not taken effect yet look
 *  identical. The fix is to drag it above the rule that shadows it. */
export interface SyncExclusionShadow {
  pattern: string
  shadowedBy: string
}

export interface SyncExclusionsResponse {
  /** The rules IN FORCE — the tenant's own list, or the seeded defaults when
   *  they have never configured one. Seeded server-side so the editor always
   *  round-trips the full list; a save that omitted a default it never
   *  rendered would silently change that folder's cap. */
  rules: SyncExclusionRule[]
  /** The seed those defaults came from, so the screen can mark a row that has
   *  been renamed or removed. NOT a floor and NOT merged under `rules`: once a
   *  tenant has saved anything, these are not consulted for them again. */
  defaults: SyncExclusionRule[]
  /** Computed over the RESOLVED list, so a rule shadowed by a default is
   *  reported too. */
  shadowed: SyncExclusionShadow[]
  limits: { maxPatterns: number; maxPatternLength: number; maxVersions: number }
}

/** Why one rule was refused. The screen renders translated copy off this,
 *  never off the server's English sentence. */
export type SyncExclusionRejection =
  | 'empty' | 'too-long' | 'absolute' | 'traversal' | 'only-wildcards'
  | 'bad-versions' | 'duplicate'

export const syncExclusionsApi = {
  async get(tenantId: string) {
    return request<SyncExclusionsResponse>(`/api/orgs/${tenantId}/sync-exclusions`)
  },
  /** Replace the list. `null` clears it back to the seeded defaults. The
   *  response echoes the NORMALISED list that was stored — render that, not
   *  what was sent, or the screen and the engine disagree from the save
   *  onwards. */
  async set(tenantId: string, rules: SyncExclusionRule[] | null, opts?: { purge?: boolean }) {
    return request<{
      ok: true
      rules: SyncExclusionRule[]
      shadowed: SyncExclusionShadow[]
      /** Present only when `purge` was sent. What was ACTUALLY removed, which
       *  can be less than the preview promised — the preview cannot see the
       *  per-file exemptions. */
      purged?: { files: number; versions: number; appVersions?: number }
      /** A big purge went to the background job (#1443). */
      queued?: { jobId: string }
    }>(
      `/api/orgs/${tenantId}/sync-exclusions`,
      { method: 'PUT', body: JSON.stringify({ rules, purge: opts?.purge === true }) },
    )
  },
  /**
   * What saving `rules` would destroy. Writes nothing.
   *
   * `willPurge: false` is the common answer — adding, raising or renaming a
   * rule destroys nothing — and the save then proceeds with no dialog.
   */
  async purgePreview(tenantId: string, rules: SyncExclusionRule[]) {
    return request<SyncExclusionPurgePreview>(
      `/api/orgs/${tenantId}/sync-exclusions/purge-preview`,
      { method: 'POST', body: JSON.stringify({ rules }) },
    )
  },
}

/** What a retention-lowering Save would delete — the purge's own plan (#1443 / #1442). */
export interface RetentionPurgePreview {
  willPurge: boolean
  files: number
  versions: number
  bytes: number
  totalBytes: number
  totalVersions: number
  appVersions: number
  /** False = too many files to ask each one: `versions` / `bytes` are an upper bound. */
  exact: boolean
  truncated: boolean
  samples: { fileId: string; relPath: string; before: number; after: number; bytes: number; totalBytes: number; rule: string | null; folderRule?: string; builtIn?: boolean }[]
  byRule: { rule: string | null; folderRule?: string; builtIn?: boolean; files: number; versions: number; bytes: number }[]
}

/** What a Save that purged reports: the real deletes, or the background job it went to. */
export interface RetentionPurgeOutcome {
  purged?: { files: number; versions: number; appVersions?: number }
  queued?: { jobId: string }
}

export interface SyncExclusionPurgePreview {
  willPurge: boolean
  files: number
  /** The purge's own count (#1442) — an upper bound only when `exact` is false. */
  versions: number
  exact?: boolean
  appVersions?: number
  byRule?: RetentionPurgePreview['byRule']
  bytes: number
  /** What the affected files hold TODAY, kept versions included — the
   *  denominator for "how much of this history goes". */
  totalBytes: number
  totalVersions: number
  samples: { relPath: string; versionCount: number; keep: number; bytes: number; totalBytes: number; rule?: string | null; folderRule?: string; builtIn?: boolean }[]
  /** The tenant is too large to count. Everything above is zero and the copy
   *  must say the number is unknown, never "nothing". */
  truncated: boolean
}

/**
 * Settings → Storage's version-retention control. Lives on the ORG router
 * (`/api/orgs/:id/version-retention`), gated `manage_org` — not the budget
 * router, since this is a drive-behavior setting with no money involved.
 */
export const versionRetentionApi = {
  async get(tenantId: string) {
    return request<VersionRetentionResponse>(`/api/orgs/${tenantId}/version-retention`)
  },
  /**
   * Writes the ceiling AND the per-kind/per-extension overrides in one call
   * — the route always replaces both `kinds`/`exts` wholesale. `ceiling:
   * null` clears the tenant's ceiling so the platform default applies again;
   * an absent/empty `kinds`/`exts` clears that map.
   */
  async set(tenantId: string, update: VersionRetentionUpdate, opts?: { purge?: boolean }) {
    return request<{ ok: true } & RetentionPurgeOutcome>(`/api/orgs/${tenantId}/version-retention`, {
      method: 'PUT',
      body: JSON.stringify({ ...update, purge: opts?.purge === true }),
    })
  },
  /** What saving `update` would delete (#1443) — the purge's own plan. Writes nothing. */
  async purgePreview(tenantId: string, update: VersionRetentionUpdate) {
    return request<RetentionPurgePreview>(`/api/orgs/${tenantId}/version-retention/purge-preview`, {
      method: 'POST',
      body: JSON.stringify(update),
    })
  },
  /** Every file with an explicit per-file override (#590) — the dedicated
   *  Version-retention page's "Per-file overrides" section, so an override
   *  never becomes invisible orphaned state. Clearing one is
   *  `filesApi.clearVersionRetention` (features/files/client) — a per-file
   *  concern, not this tenant-scoped router's. */
  async listOverrides(tenantId: string) {
    return request<VersionRetentionOverridesResponse>(`/api/orgs/${tenantId}/version-retention/overrides`)
  },
  /** Every PDApp with an explicit per-app app-version override — the "App
   *  versions" management section's twin of `listOverrides` above. Clearing
   *  one is `appVersionsApi.clearRetention` (features/files/client). */
  async listAppOverrides(tenantId: string) {
    return request<AppVersionRetentionOverridesResponse>(`/api/orgs/${tenantId}/version-retention/app-overrides`)
  },
  /** The drive-scoped file types (#590 follow-up) behind the "by file type"
   *  master-detail rail — see `DriveFileTypes`. */
  async driveTypes(tenantId: string) {
    return request<DriveFileTypes>(`/api/orgs/${tenantId}/version-retention/drive-types`)
  },
}


// ─── Chat storage, per member (#758) ─────────────────────────────────────

export interface MemberChatStorageRow {
  email: string
  role: string
  /** CAS attachment bytes: deduped within this member, drive files excluded. */
  attachmentBytes: number
  /** Inline attachment bytes — a plain sum; each row is a real copy. */
  inlineBytes: number
  /** The two added: what this member's limit is measured against. */
  totalBytes: number
  /** Bytes, or `null` for unlimited. */
  limitBytes: number | null
  /** The limit is set on this member individually, not inherited. */
  hasOwnLimit: boolean
}

export interface ChatStorageRoleDefault {
  role: string
  /** What this role gets when nobody has said otherwise. */
  shippedBytes: number | null
  /** The tenant's override. `null` here is UNLIMITED; absent means no
   *  override at all, which is why `isOverridden` is a separate flag rather
   *  than a null check. */
  overrideBytes?: number | null
  isOverridden: boolean
  /** This role cannot use the agent at all (its AI budget is 0) — a
   *  SEPARATE setting, so the note derived from it must never be inferred
   *  from the role's name. */
  aiBlocked: boolean
}

export interface ChatStoragePendingRequest {
  email: string
  name: string
  /** When they FIRST asked, ISO. Optional: a rolling deploy can serve a
   *  response from before this field existed, and a missing one must read
   *  as "unknown", never as "just now". */
  askedAt?: string
  usedLabel?: string
  limitLabel?: string
  note?: string
}

export interface ChatStorageResponse {
  members: MemberChatStorageRow[]
  /** Members waiting on an answer. A notification is read once and gone;
   *  this is what an admin opening the page tomorrow sees. */
  pendingRequests: ChatStoragePendingRequest[]
  /** Bytes whose uploader is not a current member — shown, never folded into
   *  someone, so the gap between the tenant figure and the sum of the member
   *  figures is accountable. */
  unattributedBytes: number
  roleDefaults: ChatStorageRoleDefault[]
}

/** One file inside a conversation, as the member's own page lists it. */
export interface ChatStorageFileRow {
  id: string
  /**
   * The CONTENT HASH of a CAS-backed file — an IDENTITY, not an address.
   *
   * ⚠️ NOT a handle any route takes, whatever its name says. It answers
   * "are these the same bytes as that other conversation's file", which
   * is what "takes no extra space" and `whatDeletingActuallyFrees` rest
   * on: two conversations holding one blob share this and have different
   * addressable handles. Putting it in the attachment route's path is
   * what made every file over the 10 MB inline budget a dead link
   * (fixed 2026-10-30) — that route takes `openHandle` below.
   *
   * `null` for inline bytes, which live in the message part.
   */
  handle: string | null
  /**
   * The ADDRESSABLE handle of a CAS-backed file — the opaque `att_…` the
   * attachment route resolves (`GET …/attachments/:handle`).
   *
   * `null` for the inline leg, which has none: those bytes are served by
   * the per-file route instead. Absent from an older API mid-deploy, and
   * the page must then fall back to the per-file route rather than
   * guessing — a url with `undefined` in its path is worse than a 404.
   */
  openHandle?: string | null
  name: string
  mime: string
  /** What the file actually is. */
  size: number
  /** What it costs THIS MEMBER, which is not the same number: a second
   *  reference to bytes they already uploaded, or bytes that are also a
   *  Drive file, are charged 0. */
  chargedBytes: number
  /** Set when those bytes are already counted under another of their
   *  conversations — "takes no extra space". */
  sharedWithSessionId: string | null
  sharedWithTitle: string | null
  leg: 'cas' | 'inline'
  /** When it was uploaded, in milliseconds — what the member's file-age
   *  rule is measured from. `null` when the stored timestamp could not be
   *  read, and absent from an older API mid-deploy: in both cases the
   *  page must treat it as "cannot tell" and leave the file out of the
   *  rule's preview rather than guessing it is old. */
  uploadedAt?: number | null
  /** A screenshot the AGENT took, charged to this member but not a file
   *  they attached. No Delete or Move, and never in the file-age rule's
   *  preview — the rule skips these too. Absent from an older API: then
   *  it is treated as an ordinary file, which is what that API did. */
  isSnapshot?: boolean
}

/** A live booking that takes something of hers: WHEN and WHAT, never WHO. */
export interface CleanupBooking {
  key: string
  /** The booking aimed at HER — the only one her star decides on the page. */
  own: boolean
  /** `member` — a member's conversations (hers, or a colleague's she put
   *  files in); `unattributed` — files with no recorded uploader. */
  kind?: 'member' | 'unattributed'
  /** `null` only for a booking reconstructed from an older server's
   *  undated `caughtByOtherCleanup`. */
  runAt: string | null
  includeStarred: boolean
  /** A worker is carrying it out now — its date is no longer ahead.
   *  Absent on a server that predates the field. */
  running?: boolean
}

/** One (booking, conversation) pair: going, or kept off it by her star. */
export interface CleanupTake {
  booking: string
  sessionId: string
  kept: boolean
}

/**
 * Everything the member is told about scheduled cleanups, decided by the
 * SERVER in one place (Season 2026-10-22). The page only draws it.
 */
export interface MemberCleanupView {
  bookings: CleanupBooking[]
  takes: CleanupTake[]
  /** Her own booking's conversations that could not be looked up on this
   *  request — their workspace would not open. */
  unchecked: number
  /** Conversations a take names that are not in `sessions`: her own
   *  booking's ones holding none of her files. */
  rows: BookedWithoutFilesRow[]
}

/** One of the member's own conversations on her own booking that holds none
 *  of her files. Always hers (`Session.list` resolved it), so it can be
 *  opened, starred and deleted like any row. */
export interface BookedWithoutFilesRow {
  sessionId: string
  workspaceId: string
  title: string
  updatedAt: number
  starred: boolean
  parentId: string | null
}

export interface ChatStorageSessionRow {
  /** Why this row has no `workspaceId`, when it has none. The two
   *  reasons want DIFFERENT advice:
   *    `workspace-unreadable` transient — coming back later works;
   *    `gone`                 the recount sweep clears the row for her;
   *    `workspace-missing`    the conversation database is not on disk,
   *                           and the recount REFUSES to act on that
   *                           (a wrong data dir looks identical to
   *                           everything being deleted) — so nothing
   *                           clears it on its own and she must not be
   *                           told to wait.
   *  Optional: a rolling deploy can serve a response from before the
   *  field existed. */
  unconfirmed?: 'workspace-unreadable' | 'workspace-missing' | 'gone'
  sessionId: string
  /** `null` when the conversation could not be resolved to a title — it may
   *  have been deleted, or live in a workspace that would not open. The row
   *  is still listed: its bytes are real and the figure above counts them. */
  title: string | null
  updatedAt: number
  /** The workspace the conversation lives in, and the ONLY rows the member
   *  may delete from this page. The server sets it for a conversation its
   *  own `Session.list` returned for this member — the same filter the chat
   *  list uses — so it doubles as proof the conversation is theirs. `null`
   *  for a row her list did not return: its workspace would not open, or
   *  the conversation is gone while its file rows remain (only an owner
   *  can write into a conversation, so it is never somebody else's —
   *  #1280 review #11). It is still listed and still counted. */
  workspaceId: string | null
  /** The FOLDER of that workspace — what a project route opens (`workspaceId`
   *  is the workspace record id). Null with `workspaceId`, or when the record
   *  is gone. Absent from an older API mid-deploy. Used where the deployment
   *  has no chat home (#368). */
  workspaceFolderId?: string | null
  bytes: number
  /** The files inside it, so a member can drop one and keep the thread. */
  files: ChatStorageFileRow[]
  fileCount: number
  /** The member said "keep this" — it bypasses their own auto-delete
   *  rule, though an admin's cleanup can be told to ignore it. */
  starred: boolean
  /** Set when this conversation is a BRANCH of another, so the list can
   *  nest it under its parent. */
  parentId: string | null
}

/** A cleanup an admin has booked. The member list badges these and offers
 *  to cancel; the dialog refuses a second one for the same target. */
export interface ScheduledCleanupRow {
  id: string
  target: { kind: 'member'; email: string } | { kind: 'unattributed' }
  idleDays: number
  includeStarred: boolean
  /** ISO. The date the member was told. */
  runAt: string
  createdBy: string
  /** ⚠️ `running` IS IN THIS LIST, on purpose — the badge must not vanish
   *  while a worker is carrying a booking out. But a `running` row cannot
   *  be cancelled (`cancel` is conditional on `scheduled`), so the menu
   *  must not offer it: that button answered 409 every time. */
  state: 'scheduled' | 'running'
}

/** One row of the admin's Cleanup activity dialog (B2 step ③). Counts are
 *  LOWER bounds — the page says "at least" (D34). */
export interface CleanupActivityRow {
  id: string
  target: { kind: 'member'; email: string } | { kind: 'unattributed' }
  state: 'scheduled' | 'running' | 'done' | 'cancelled' | 'failed'
  runAt: string
  startedAt: string | null
  finishedAt: string | null
  idleDays: number
  /** How many conversations its frozen list holds; `null` = it has no
   *  list (it re-plans from its criteria) — never "0". */
  conversations: number | null
  /** `null` = nothing was ever counted — not "0". */
  deletedCount: number | null
  freedBytes: number | null
  /**
   * What left the MEMBERS' OWN figures — deduped per content hash within
   * each member, drive files excluded, i.e. what their Storage page counts
   * by. `freedBytes` is what the conversations HELD, an upper bound.
   *
   * `null` = no figure to state: a booking that ran before the column
   * existed, or one whose every pass released nothing. Never render it as
   * "0" — show the upper bound instead, as the row's copy does.
   * Optional: an older server does not send it.
   */
  releasedBytes?: number | null
  /**
   * What left the ORGANISATION's own figure — one charge per content hash
   * for the whole tenant, so a file two members uploaded counts ONCE and
   * not at all while either of them still has it. `releasedBytes` charges
   * that same file to each of their limits (it is stored once but counted
   * against both). That relation holds FOR ONE MEASUREMENT only: the two
   * columns are accumulated by two independent attempts and can disagree
   * when one of them fails, which is why the row checks it before stating
   * the second figure rather than trusting it.
   *
   * Shown only where it DIFFERS from `releasedBytes`, with the sentence
   * that explains why — otherwise it says nothing the other does not.
   * `null` = no figure to state (a booking older than the column, or one
   * whose every pass released nothing to the tenant); never render "0".
   * Optional: an older server does not send it.
   */
  orgReleasedBytes?: number | null
  /**
   * Some chunk DROPPED the members' figure, so `releasedBytes` is short by
   * a whole chunk and the row must say "at least".
   *
   * ⚠️ NULL IS NOT `false`: NULL = unknown (a booking older than the
   * column, or one that recorded no chunk) and MUST hedge; `false` = the
   * figure is complete and may be stated exactly. Treating NULL as `false`
   * states a short figure as exact, which is the only direction this may
   * not be wrong in. Optional: an older server does not send it.
   */
  releasedLost?: boolean | null
  /** The same for `orgReleasedBytes`. Separate because the two figures are
   *  computed by two queries and are lost independently. */
  orgReleasedLost?: boolean | null
  /** `unrecorded` = a run-now that deleted and could not write it down. */
  failureKind: 'retries' | 'abandoned' | 'unrecorded' | 'error' | null
  /** `cancelled` rows: an admin STOPPED it while running — `stopping`
   *  until the worker has finished the batch in hand, then `stopped`.
   *  Optional: an older server does not send it. */
  stop?: 'stopping' | 'stopped' | null
  /** Scheduled again after a pass — in progress, not untouched. */
  resumed?: boolean
  /** Scheduled, its time has come, no worker has taken it yet — in
   *  progress too (the server's `cleanupUnderWay`). Optional: an older
   *  server does not send it. */
  due?: boolean
}

export interface ChatStorageCleanupResult {
  applied: boolean
  /** How many conversations matched (a preview) or were frozen into the
   *  booking (an apply) — the number the booking will work through. */
  conversations: number
  /** An UPPER BOUND on what is freed — see `cleanUpChatStorage`. */
  bytes: number
  /**
   * The booking starts NOW (D35): the worker takes it within a minute and
   * clears the whole list in the background. It has deleted NOTHING yet —
   * "started", never "cleared". Absent on a preview.
   */
  startsNow?: boolean
  /** The booking's id. */
  id?: string
  /** ISO, when it runs (now, for a start-now). */
  runAt?: string
  /** Owners who were told. Not every owner has an account to tell. */
  notified?: number
  /** Workspaces nothing could be proven about, so they were left alone. A
   *  non-zero number means this answer is incomplete, and the dialog says
   *  so rather than presenting it as the whole picture. */
  workspacesSkipped: number
  /** The list bound was reached: there is more past this window than one
   *  booking can freeze. */
  truncated: boolean
}

export const storageApi = {
  /** Every member's chat-storage usage and limit (#758). Owner/admin only;
   *  the page hides the section's org view on 403 rather than erroring. */
  /** The caller's own conversations and what each costs them. Served by the
   *  AGENT, not by orgs: the title lives in the per-workspace SQLite and the
   *  bytes in Postgres, and only that feature can read both. */
  async getMyChatStorageSessions() {
    return request<{
      sessions: ChatStorageSessionRow[]
      /**
       * A cleanup an admin booked against THIS member, or null.
       *
       * ⚠️ `caughtSessionIds` IS THE ANSWER; the criteria are context.
       * A booking freezes its list when it is made (D31), so working the
       * set out from `idleDays` is wrong in both directions — and the
       * dangerous direction is the one where a conversation the member
       * has used since the booking still goes, while a page deriving
       * from `idleDays` would show it as safe. The page falls back to
       * the criteria only for a server that predates this field.
       *
       * `null` for a run-now booking, which finishes inside the request
       * that created it and so cannot be seen here.
       */
      scheduledCleanup: {
        runAt: string
        idleDays: number
        includeStarred: boolean
        /** Absent from a server that predates this field — that is the
         *  rolling-deploy shape the page's fallback arm exists for, so
         *  it has to be expressible here. `null` means the booking has
         *  no frozen list. ⚠️ NO PRODUCER IS CURRENTLY DEMONSTRABLE: not a
         *  legacy row (0309 created the table on this same branch), not
         *  deploy skew (0306 and 0309 ship in one commit), and not a
         *  run-now (the route's carve-out drops it before this field is
         *  built). Treat it as a defensive default, not as "a run-now" —
         *  reading it that way re-justifies the `idleDays` fallback for
         *  conversations that are already deleted. See the route (round
         *  13, finding 7). `undefined`/absent is a DIFFERENT case and is
         *  real: an older API instance serving this page omits the field. */
        caughtSessionIds?: string[] | null
      } | null
      /**
       * Conversations on SOMEBODY ELSE'S live booking.
       *
       * This page lists conversations by who UPLOADED into them, so it
       * shows colleagues' threads the member put files in. A cleanup of
       * that colleague takes those bytes, and `scheduledCleanup` —
       * which is only the booking aimed at this member — cannot say so.
       *
       * The member's OWN booking is deliberately NOT in here: the page
       * applies the star rule to that one, and a set consulted ahead of
       * the star made starring look like it had changed nothing.
       * Intersected server-side with the rows this response carries, so
       * it discloses nothing the page is not already showing.
       * Absent on a server that predates the field.
       */
      caughtByOtherCleanup?: string[]
      /** The one answer the page draws from. Absent on a server that
       *  predates it, where the page rebuilds it from the two fields
       *  above. */
      cleanup?: MemberCleanupView
      /** The member's OWN standing auto-delete rule, in days. `null` =
       *  no rule, which is where everybody starts. */
      autoDeleteDays: number | null
      /** The member's OWN standing rule for OLD FILES, in days. `null` =
       *  no rule. A SEPARATE number from `autoDeleteDays`, which is about
       *  idle conversations — see the server route's docblock. Absent
       *  from an older API mid-deploy. */
      fileAutoDeleteDays?: number | null
    }>('/api/agent/sessions/chat-storage')
  },

  /** Set or clear the member's OWN auto-delete rule. `null` switches it
   *  off — never a zero, which would read as "delete everything". */
  async setChatAutoDelete(days: number | null) {
    return request<{ ok: true; days: number | null }>('/api/agent/sessions/chat-storage/auto-delete', {
      method: 'PUT',
      body: JSON.stringify({ days }),
    })
  },

  /** Set or clear the member's OWN rule for OLD FILES. `null` switches it
   *  off. Separate from `setChatAutoDelete`: that one is about idle
   *  conversations and takes the whole conversation. */
  async setChatFileAutoDelete(days: number | null) {
    return request<{ ok: true; days: number | null }>('/api/agent/sessions/chat-storage/file-auto-delete', {
      method: 'PUT',
      body: JSON.stringify({ days }),
    })
  },

  /** The member deleting ONE of their own conversations from the Storage
   *  page — their half of the cleanup surface, and the only move that
   *  actually gives them space back while they are blocked.
   *
   *  Deliberately the SAME route the chat's own delete uses, rather than a
   *  cleanup endpoint of its own: that delete is seven ordered steps whose
   *  order was learned from bugs, and a second way in is where the two
   *  copies drift. Only called for a row with a `workspaceId`. */
  async deleteMyChatStorageSession(sessionId: string, workspaceId: string) {
    return request<{ id: string }>(
      `/api/agent/sessions/${encodeURIComponent(sessionId)}?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: 'DELETE' },
    )
  },

  /** Remove ONE file from a conversation. Works for both legs — a CAS
   *  file's parts are resolved server-side, because this page has no part
   *  id for them. Only offered on a row with a `workspaceId`. */
  async deleteChatStorageFile(sessionId: string, workspaceId: string, fileId: string) {
    // The member's OWN calendar day, for the "deleted on" note the
    // conversation keeps — the server stamps UTC otherwise, which in
    // Malaysia reads as the day before until 8am (D-d).
    const now = new Date()
    const localDay = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    // `spacePending`: deleted, but its space was not released yet — the
    // figure catches up at the next recount (D-a).
    return request<{ ok: true; leg: string; spacePending?: true }>(
      `/api/agent/sessions/${encodeURIComponent(sessionId)}/chat-storage/files/${encodeURIComponent(fileId)}?workspaceId=${encodeURIComponent(workspaceId)}&localDay=${localDay}`,
      { method: 'DELETE' },
    )
  },

  /** Move one chat file into the Drive — the move that frees the quota
   *  and loses nothing: the bytes become a Drive file and the
   *  conversation keeps a chip that opens it.
   *
   *  Keyed on the FILE ROW, not a CAS handle, so a pasted image works
   *  too: inline bytes have no handle, and the old handle-keyed endpoint
   *  could not be called for them at all. The server materialises them
   *  and then runs the same save. */
  async moveChatFileToDrive(sessionId: string, workspaceId: string, fileId: string) {
    return request<{ ok: true; fileId: string; path: string; name: string }>(
      `/api/agent/sessions/${encodeURIComponent(sessionId)}/chat-storage/files/${encodeURIComponent(fileId)}/move-to-drive?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: 'POST', body: JSON.stringify({}) },
    )
  },

  /** Star or unstar a conversation from the storage page — the same
   *  PATCH the chat's own session list uses. */
  async setChatStorageSessionStarred(sessionId: string, workspaceId: string, starred: boolean) {
    return request<unknown>(
      `/api/agent/sessions/${encodeURIComponent(sessionId)}?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: 'PATCH', body: JSON.stringify({ starred }) },
    )
  },

  /** The admin's `Clean up` / `Force clean up`: delete conversations in
   *  this organisation that nothing has happened in for `idleDays`.
   *
   *  `apply: false` (the default) PREVIEWS — it writes nothing and returns
   *  what would go. The dialog calls it on every change to the day count
   *  and only ever sends `apply: true` from the confirm button.
   *
   *  `bytes` is an UPPER BOUND. The organisation gets back less whenever a
   *  file is also referenced by another conversation or by a Drive file;
   *  this sums the rows without the quota's deduplication. Overstating is
   *  the safe direction for a delete confirmation, and the copy says
   *  "up to". Served by the agent — "idle" is a column in its SQLite. */
  async cleanUpChatStorage(args: {
    target: 'member' | 'unattributed'
    email?: string
    /** Conversations idle longer than this are in scope. 1–3650. */
    idleDays: number
    /** A star is the member's own "keep this". Off unless the admin says so. */
    includeStarred?: boolean
    /** False (the default) PREVIEWS and writes nothing. */
    apply?: boolean
    /** MINUTES from now, so the dialog can offer hours. 0 runs it
     *  immediately — drawn in red and asked a second time, because
     *  nobody gets any notice at all. */
    runInMinutes?: number
  }) {
    return request<ChatStorageCleanupResult>('/api/agent/sessions/chat-storage/cleanup', {
      method: 'POST',
      body: JSON.stringify(args),
    })
  },

  /** What is booked in this organisation — the SCHEDULED badges. */
  async listChatStorageCleanups() {
    return request<{ cleanups: ScheduledCleanupRow[] }>('/api/agent/sessions/chat-storage/cleanups')
  },

  /** Recent cleanups — booked, running, finished, cancelled, given up. */
  async listChatStorageCleanupActivity() {
    return request<{ cleanups: CleanupActivityRow[] }>('/api/agent/sessions/chat-storage/cleanups/activity')
  },

  /** Cancel a booking. 409 when the worker already claimed it — by then
   *  the conversations are gone, and the caller must not say otherwise. */
  async cancelChatStorageCleanup(id: string) {
    // `stopping`: it was running, and stops after the batch in hand (D35).
    // `result` / `deletedCount`: what it did, for the sentence after it
    // (⑤). Optional: an older server sends neither.
    return request<{ ok: true; stopping?: boolean; result?: 'cancelled' | 'stopped' | 'stopping'; deletedCount?: number | null }>(`/api/agent/sessions/chat-storage/cleanups/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    })
  },

  /** The member asking an admin to raise their limit. Their only recourse
   *  — their own page cannot create space the organisation has not got. */
  async requestMoreChatStorage(tenantId: string, note?: string) {
    // `notified` is how many bells actually LIT — `notifyUserByEmail`
    // reports that, so an admin whose address has no account yet counts
    // as not reached. `eligible` is how many could have been asked at
    // all; `eligible === 0` means nobody in the org holds the capability,
    // which retrying will not fix. Both optional: an older API omits
    // them, and `undefined === 0` is false, so that reads as success —
    // the same answer it gave before they existed.
    return request<{ ok: true; alreadyPending?: boolean; notified?: number; eligible?: number }>(
      `/api/orgs/${tenantId}/chat-storage/request-more`,
      { method: 'POST', body: JSON.stringify({ note }) },
    )
  },

  async getChatStorage(tenantId: string) {
    return request<ChatStorageResponse>(`/api/orgs/${tenantId}/chat-storage`)
  },

  /** The caller's own row.
   *
   *  Handle `member: null` defensively (never as "0 of 0"), but it is not a
   *  state this route produces — it 403s a caller with no membership.
   *
   *  And it is NOT the personal-drive case. A personal tenant carries one
   *  `owner` membership and gets a real row, with `limitBytes: null`. The
   *  claim that it has no membership is the one review round 1 found had
   *  capped every personal drive at 500 MB; it was corrected in five other
   *  docblocks and survived here (full review 2, finding 9). Do not
   *  reintroduce it. */
  async getMyChatStorage(tenantId: string) {
    return request<{ member: MemberChatStorageRow | null }>(`/api/orgs/${tenantId}/chat-storage/mine`)
  },

  /** Set one member's limit. `null` CLEARS it so they inherit their role's
   *  default again — never write the number that default happens to be
   *  today, or the next change to it silently skips them. */
  async setMemberChatStorageLimit(tenantId: string, email: string, limitBytes: number | null, note?: string) {
    return request<{ ok: true; inherits: boolean }>(`/api/orgs/${tenantId}/members/chat-storage`, {
      method: 'PATCH',
      body: JSON.stringify(note ? { email, limitBytes, note } : { email, limitBytes }),
    })
  },

  /** An admin saying no. Saying no is a REPLY — without it the member's
   *  only signal is silence, which they cannot tell from "not looked at
   *  yet", so they ask again. */
  /** `alreadyAnswered` means another admin got there first: the route sent
   *  no second, contradicting reply, so neither should the caller claim
   *  one. */
  async declineChatStorageRequest(tenantId: string, email: string, asOrgEmail?: string | null) {
    return request<{ ok: true; alreadyAnswered?: boolean; memberGone?: boolean }>(`/api/orgs/${tenantId}/chat-storage/decline`, {
      method: 'POST',
      body: JSON.stringify({ email }),
      // ACTING-AS headers, the same pair `org-connections` uses for the
      // same situation: the bell can show a request from an organisation
      // the user is not currently acting in, and `getOrgEmail(auth)` would
      // then hand the route the membership email of the WRONG tenant.
      // Where a person's address differs between their two organisations —
      // a shape this product supports — the route 403s and the UI says
      // "Could not send that reply" (scoped review of full review 2,
      // finding 5). Omitted when the caller is already in that tenant.
      ...(asOrgEmail ? { headers: { 'x-tenant-id': tenantId, 'x-org-email': asOrgEmail } } : {}),
    })
  },

  /** Set one role's default. OMITTING `limitBytes` resets the role to the
   *  shipped default; passing a number pins it. Two different writes, hence
   *  the optional argument.
   *
   *  ⚠️ `number`, NOT `number | null`. `null` used to mean UNLIMITED and the
   *  route refuses it since 2026-10-30 (owner's decision). Typed nullable,
   *  a caller following the old docblock would compile, ship, and meet a
   *  400 that this dialog never shows a reason for — `save()`'s bare catch
   *  renders the generic failure, and because it writes roles in sequence
   *  every role BEFORE the failing one is already written and audited.
   *  Narrow here so that is a compile error instead. */
  async setRoleChatStorageLimit(tenantId: string, role: string, limitBytes?: number) {
    return request<{ ok: true; reset: boolean }>(`/api/orgs/${tenantId}/roles/chat-storage`, {
      method: 'PATCH',
      body: JSON.stringify(limitBytes === undefined ? { role } : { role, limitBytes }),
    })
  },

  async getComposition(tenantId: string) {
    return request<StorageCompositionResponse>(`/api/orgs/${tenantId}/storage`)
  },

  /** Largest folders by subtree size (followups §3.1). Owner/admin role
   *  required server-side (folder names + weights cross folder-permission
   *  boundaries); the page hides the card on 403 rather than erroring. */
  async listLargestFolders(tenantId: string) {
    return request<{ folders: LargestFolder[] }>(`/api/orgs/${tenantId}/storage/folders`)
  },

  /** Duplicate sets for the review modal (followups §2.1). Owner/admin
   *  role required server-side — same §4.3 stance as the drill-down. */
  async listDuplicates(tenantId: string) {
    return request<DuplicatesResponse>(`/api/orgs/${tenantId}/storage/duplicates`)
  },

  /** What "Clear old versions" would delete, grouped per file — the
   *  versions review modal's feed. Owner/admin role required server-side
   *  (same §4.3 stance as the drill-down). */
  async listVersions(tenantId: string) {
    return request<VersionsResponse>(`/api/orgs/${tenantId}/storage/versions`)
  },

  /** Clear old versions (followups §2.2) — permanently deletes the §2.8
   *  exclusion set (exactly what the "Old versions" line counts), or only
   *  `versionIds` when given (the modal's checkbox selection; the
   *  non-current guard is server-side either way). Owner/admin role
   *  required server-side. */
  async purgeVersions(tenantId: string, versionIds?: string[]): Promise<PurgeVersionsResult> {
    const post = (ids?: string[]) =>
      request<PurgeVersionsResult>(
        `/api/orgs/${encodeURIComponent(tenantId)}/storage/versions/purge`,
        {
          method: 'POST',
          ...(ids ? {
            body: JSON.stringify({ versionIds: ids }),
            headers: { 'Content-Type': 'application/json' },
          } : {}),
        },
      )

    // No selection = purge everything, one call, no cap involved.
    if (!versionIds) return post()

    // The route rejects >500 ids. The modal can offer far more than that
    // — it lists up to 100 files and a file keeps up to 50 versions, so a
    // few group-level "select all" clicks reach several thousand — and a
    // single oversized call failed AFTER the user confirmed a destructive
    // dialog, with the server's field-name validation text and nothing
    // deleted (review 2026-08-06). Chunk here rather than in the modal so
    // every caller is covered, and SEQUENTIALLY: each call also runs a
    // blob-collection pass server-side, which is not something to fan out.
    const CHUNK = 500
    if (versionIds.length <= CHUNK) return post(versionIds)

    const total: PurgeVersionsResult = { success: true, deleted: 0, freedBytes: 0, blobsRemoved: 0 }
    for (let i = 0; i < versionIds.length; i += CHUNK) {
      // Deliberately NOT caught: a mid-way failure propagates with the
      // earlier chunks already committed, so the caller must reload rather
      // than assume nothing happened. VersionsModal reloads in `finally`.
      const r = await post(versionIds.slice(i, i + CHUNK))
      total.deleted += r.deleted ?? 0
      total.freedBytes += r.freedBytes ?? 0
      total.blobsRemoved += r.blobsRemoved ?? 0
    }
    return total
  },

  /** Drill-down for the manage modal. Owner/admin role required server-side
   *  (file names cross folder-permission boundaries — plan §4.3); a plain
   *  org member gets a 403 the modal renders as copy. `minBytes` is the
   *  large-files-only floor — filtered in SQL, 0 = everything. */
  async listCategoryFiles(tenantId: string, category: string, minBytes = 0) {
    const floor = minBytes > 0 ? `&minBytes=${minBytes}` : ''
    return request<CategoryFilesResponse>(`/api/orgs/${tenantId}/storage/files?category=${encodeURIComponent(category)}${floor}`)
  },
}
